import HomePage from '@pages/home';
import { LayoutBase, LayoutNoHeader, LayoutSideMenu, LayoutSimple } from 'layouts';
import { ReactNode, useContext, useEffect } from 'react';
import {
	Administration,
	Aggregate,
	BusinessIntelligencePage,
	Clients,
	Icone,
	LoginSSO,
	NotFoundPage,
	Opportunity,
	Profile,
	Project,
	Projects,
	Reports,
	Setup,
	Tenders,
	PrivacyPolicyToAccept,
	DownloadFromLink,
} from './pages';
import { UserContext } from '@components/contexts/UserProvider';
import { logout } from '@lib/api';
import { Redirect } from 'react-router-dom';
import { PermissionWrapper } from '@components/permissions/PermissionWrapper';
import DpaPreview from '@pages/dpa-preview';
import AggregateBusinessIntelligencePage from '@pages/aggregate-business-intelligence';

type PublicRoute = {
	name: string;
	url: string;
	component: () => ReactNode;
	type: 'public';
};

type PrivateRoute = {
	name: string;
	url: string;
	component: () => ReactNode;
	type: 'private';
};

const LogoutRoute = () => {
	const { logoutContext } = useContext(UserContext);

	useEffect(() => {
		logoutContext();
		logout();
	}, [logoutContext]);

	return <Redirect to="/" />;
};

const publicRoutes = [
	{
		name: 'home',
		url: '/',
		component: () => (
			<LayoutNoHeader>
				<HomePage />
			</LayoutNoHeader>
		),
		type: 'public',
	},
	{
		name: 'icone',
		url: '/icone',
		component: () => (
			<LayoutSimple>
				<Icone />
			</LayoutSimple>
		),
		type: 'public',
	},
	{
		name: 'logout',
		url: '/logout',
		component: () => <LogoutRoute />,
		type: 'public',
	},
	{
		name: 'kc',
		url: '/kc',
		component: () => (
			<LayoutNoHeader>
				<LoginSSO />
			</LayoutNoHeader>
		),
		type: 'public',
	},
	{
		name: 'download',
		url: '/download',
		component: () => (
			<LayoutBase>
				<DownloadFromLink />
			</LayoutBase>
		),
		type: 'public',
	},
] as const satisfies readonly PublicRoute[];

const privateRoutes = [
	{
		name: 'projects',
		url: '/projects',
		component: () => (
			<PermissionWrapper>
				{(permissions) => (
					<LayoutSimple>
						<Projects permissions={permissions} />
					</LayoutSimple>
				)}
			</PermissionWrapper>
		),
		type: 'private',
	},
	{
		name: 'project',
		url: '/project/:projectId/:tab?/:submenu?/:subtab?',
		component: () => (
			<PermissionWrapper>
				{(permissions) => (
					<LayoutBase>
						<Project permissions={permissions} />
					</LayoutBase>
				)}
			</PermissionWrapper>
		),
		type: 'private',
	},
	{
		name: 'aggregate',
		url: '/projects/aggregate-detail/:aggregateId',
		component: () => (
			<LayoutBase>
				<Aggregate />
			</LayoutBase>
		),
		type: 'private',
	},
	{
		name: 'administration',
		url: '/administration',
		component: () => (
			<LayoutBase>
				<Administration />
			</LayoutBase>
		),
		type: 'private',
	},
	{
		name: 'clients',
		url: '/clients',
		component: () => (
			<LayoutBase>
				<Clients />
			</LayoutBase>
		),
		type: 'private',
	},
	{
		name: 'setup',
		url: '/setup',
		component: () => (
			<LayoutBase>
				<Setup />
			</LayoutBase>
		),
		type: 'private',
	},
	{
		name: 'tenders',
		url: '/tenders',
		component: () => (
			<LayoutSideMenu>
				<Tenders />
			</LayoutSideMenu>
		),
		type: 'private',
	},
	{
		name: 'opportunity',
		url: '/opportunity',
		component: () => (
			<LayoutBase>
				<Opportunity />
			</LayoutBase>
		),
		type: 'private',
	},
	{
		name: 'reports',
		url: '/reports',
		component: () => (
			<LayoutSimple>
				<Reports />
			</LayoutSimple>
		),
		type: 'private',
	},
	{
		name: 'profile',
		url: '/profile',
		component: () => (
			<LayoutSideMenu>
				<Profile />
			</LayoutSideMenu>
		),
		type: 'private',
	},
	{
		name: 'privacy-policy-to-accept',
		url: '/privacy-policy-to-accept',
		component: () => (
			<LayoutNoHeader>
				<PrivacyPolicyToAccept />
			</LayoutNoHeader>
		),
		type: 'private',
	},
	{
		name: 'dpa',
		url: '/dpa',
		component: () => (
			<LayoutSimple>
				<DpaPreview />
			</LayoutSimple>
		),
		type: 'private',
	},
	{
		name: 'key-business-intelligence',
		url: '/:projectId/business-intelligence',
		component: () => (
			<LayoutBase>
				<BusinessIntelligencePage />
			</LayoutBase>
		),
		type: 'private',
	},
	{
		name: 'aggregate-business-intelligence',
		url: '/business-intelligence/projectaggregates/:aggregateId',
		component: () => (
			<LayoutBase>
				<AggregateBusinessIntelligencePage />
			</LayoutBase>
		),
		type: 'private',
	},
] as const satisfies readonly PrivateRoute[];

const notFoundRoute = {
	name: '404',
	url: '*',
	component: () => (
		<LayoutBase>
			<NotFoundPage />
		</LayoutBase>
	),
	type: 'public',
} as const satisfies PublicRoute;

export const routes = [...publicRoutes, ...privateRoutes, notFoundRoute];
